import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ElxContainer, ElxMessageBarType } from "@elixir/components";
import { Tile } from "../../commoncomponents/Tile";
import { useRootContext } from "../../library/providers/root/RootContextProvider";
import { useLocaleStrings } from "../../library/providers/strings";
import { getPostSalesSellerLink, getAbsCopilotLink } from "./Escalations.helpers";
import { specialEventsTileToEscalationSetting } from "./WorldEventsTile.helpers";
import "./EscalationCustom.css";
import { useEfdContainerStyles } from "./Escalations.styles";
import { RecommendationInput, useCardRecommendation } from "./RecommendationInput";
import { logEvent } from "../../library/logging/loggingService";
import { useLoadingAnnouncement } from "../../library/providers/loadingAnnouncement";
/**
 * Escalations Component
 * Renders the main landing page displaying available escalation types as navigable tiles.
 * Tiles are conditionally displayed based on user privileges (e.g., Executive access).
 */
const Escalations = () => {
    var _a;
    // Hook to retrieve localized strings for internationalization support
    const { getLocalSpecificString } = useLocaleStrings();
    const { announce } = useLoadingAnnouncement();
    // Extract user privileges and app configuration from global context.
    const { privileges: { isExecutive }, appConfiguration, } = useRootContext();
    // Backend returns config values as strings; coerce to boolean safely.
    // Mirrors the established pattern used for isEUEnabled across the codebase.
    const isAIRecommendationEnabled = String((_a = appConfiguration === null || appConfiguration === void 0 ? void 0 : appConfiguration.aiCardRecommendationEnabled) !== null && _a !== void 0 ? _a : "").trim().toLowerCase() === "true";
    // AI card recommendation — all business logic lives in the hook.
    // The `enabled` flag lets the hook short-circuit without network or agent
    // initialisation when the feature flag is off, while still satisfying the
    // React rules-of-hooks requirement that hooks are called unconditionally.
    const { recommendedCardId, isLoading: isRecommendationLoading, statusHint: recommendationStatusHint, handleQueryReady, } = useCardRecommendation({ enabled: isAIRecommendationEnabled });
    /** Fires telemetry when any card is clicked while the AI feature is enabled. */
    const handleCardClick = (clickedCardId) => {
        if (!isAIRecommendationEnabled)
            return;
        if (recommendedCardId) {
            logEvent("CardRecommendation.CardClicked", {
                component: "Escalations",
                action: "card_clicked",
                clickedCardId: clickedCardId !== null && clickedCardId !== void 0 ? clickedCardId : "unknown",
                recommendedCardId,
                wasRecommended: clickedCardId === recommendedCardId,
            });
        }
        else {
            logEvent("CardRecommendation.Skipped", {
                component: "Escalations",
                action: "skipped",
                clickedCardId: clickedCardId !== null && clickedCardId !== void 0 ? clickedCardId : "unknown",
            });
        }
    };
    // Executive escalation tile - only shown to users with executive privileges
    const executiveTile = [
        {
            title: getLocalSpecificString("executiveTitle"),
            description: getLocalSpecificString("executiveDescription"),
            link: getLocalSpecificString("executiveLink"),
            linkName: getLocalSpecificString("executiveLinkName"),
            name: getLocalSpecificString("executiveTitle"),
            cardId: "executive",
        },
    ];
    // Configuration array for all escalation tiles displayed on the landing page
    // Each tile represents a different escalation category with localized content
    const escalationSettings = [
        {
            title: getLocalSpecificString("consumerTitle"),
            description: getLocalSpecificString("consumerDescription"),
            link: getLocalSpecificString("consumerLink"),
            linkName: getLocalSpecificString("consumerLinkName"),
            name: getLocalSpecificString("consumerTitle"),
            cardId: "consumer",
        },
        // Volume Licensing/M365-Azure Billing/Licensing Documentation Errors - escalation tile - for enterprise customers and partners
        {
            title: getLocalSpecificString("volumeLicensingM365AzureTitle"),
            description: getLocalSpecificString("volumeLicensingM365AzureDescription"),
            link: getLocalSpecificString("volumeLicensingM365AzureLink"),
            linkName: getLocalSpecificString("volumeLicensingM365AzureLinkName"),
            name: getLocalSpecificString("volumeLicensingM365AzureTitle"),
            cardId: "volumeLicensingM365Azure",
        },
        // Post Sales Seller Support Team (PSSST) tile - for post sales support
        {
            title: getLocalSpecificString("postSalesSellerTitle"),
            description: getLocalSpecificString("postSalesSellerDescription"),
            link: getPostSalesSellerLink(),
            linkName: getLocalSpecificString("postSalesSellerLinkName"),
            name: getLocalSpecificString("postSalesSellerTitle"),
            cardId: "postSalesSeller",
        },
        // Privacy escalation tile - for privacy-related issues
        {
            title: getLocalSpecificString("privacyTitle"),
            description: getLocalSpecificString("privacyDescription"),
            link: getLocalSpecificString("privacyLink"),
            linkName: getLocalSpecificString("privacyLinkName"),
            name: getLocalSpecificString("privacyTitle"),
            cardId: "privacy",
        },
        // Technical product issue tile - for Azure service-specific technical escalations
        {
            title: getLocalSpecificString("technicalProductTitle"),
            description: getLocalSpecificString("technicalDescription"),
            link: getLocalSpecificString("technicalLink"),
            linkName: getLocalSpecificString("technicalLinkName"),
            name: getLocalSpecificString("technicalProductTitle"),
            cardId: "technicalProduct",
        },
        // Azure confidence calls & Technical (non-quota) escalation tile
        {
            title: getLocalSpecificString("customerConfidenceCallTitle"),
            description: getLocalSpecificString("customerConfidenceCallDescription"),
            link: getLocalSpecificString("customerConfidenceCallLink"),
            linkName: getLocalSpecificString("customerConfidenceCallLinkName"),
            name: getLocalSpecificString("customerConfidenceCallTitle"),
            cardId: "customerConfidenceCall",
        },
        // ABS Copilot tile - for Azure Business Support Copilot escalations
        {
            title: getLocalSpecificString("absCopilotTitle"),
            description: getLocalSpecificString("absCopilotDescription"),
            link: getAbsCopilotLink(),
            linkName: getLocalSpecificString("absCopilotLinkName"),
            name: getLocalSpecificString("absCopilotTitle"),
            cardId: "absCopilot",
        },
        // Partner program tile - for specialized partner escalation paths
        {
            title: getLocalSpecificString("partnerProgramTitle"),
            description: getLocalSpecificString("partnerProgramDescription"),
            link: getLocalSpecificString("partnerProgramLink"),
            linkName: getLocalSpecificString("partnerProgramLinkName"),
            name: getLocalSpecificString("partnerProgramTitle"),
            cardId: "partnerProgram",
        },
        // Conditionally include executive tile only if user has executive privileges
        ...(isExecutive ? executiveTile : []),
        // Special programs tile - for specialized escalation programs
        {
            title: getLocalSpecificString("specialprogramTitle"),
            description: getLocalSpecificString("specialprogramDescription"),
            link: getLocalSpecificString("specialprogramLink"),
            linkName: getLocalSpecificString("specialprogramLinkName"),
            name: getLocalSpecificString("specialprogramTitle"),
            cardId: "specialprogram",
        },
    ];
    // Backend gates the Special Events tile via feature flag + content validation.
    // If specialEventsTile is non-null the tile is render-ready; otherwise it is hidden.
    const specialEventsTile = appConfiguration === null || appConfiguration === void 0 ? void 0 : appConfiguration.specialEventsTile;
    if (specialEventsTile) {
        escalationSettings.push(specialEventsTileToEscalationSetting(specialEventsTile));
    }
    // Configuration for the informational message banner displayed at the top of the page
    const messageInfo = {
        message: getLocalSpecificString("escalationInfoMessage"),
        type: ElxMessageBarType.info,
        iconProps: { iconName: "InfoSolid" },
    };
    // Render semantic heading elements for accessibility
    const renderHeader = () => {
        return (_jsxs("div", Object.assign({ className: "escalation-header-content" }, { children: [_jsx("h1", Object.assign({ className: "container-heading" }, { children: getLocalSpecificString("pageHeader") })), _jsx("h2", Object.assign({ className: "container-subheader" }, { children: getLocalSpecificString("pageSubHeader") }))] })));
    };
    return (_jsx("div", Object.assign({ className: `${useEfdContainerStyles} landingContainer` }, { children: _jsxs(ElxContainer, Object.assign({ headerContent: renderHeader(), fillBackground: true, message: messageInfo }, { children: [isAIRecommendationEnabled && (_jsx(RecommendationInput, { onQueryReady: handleQueryReady, isLoading: isRecommendationLoading, recommendedCardId: recommendedCardId, statusHint: recommendationStatusHint })), escalationSettings.map((escalation) => {
                    const isCustomerConfidenceCall = escalation.cardId === "customerConfidenceCall";
                    return (_jsx("div", Object.assign({ className: `tileContainer ${isCustomerConfidenceCall ? "customer-confidence-call" : ""}`, role: "presentation", onClick: () => { if (escalation.link)
                            handleCardClick(escalation.cardId); } }, { children: _jsx(Tile, { "aria-label": escalation.name, name: escalation.name, title: escalation.title, titleAs: "h3", description: escalation.description, link: escalation.link, separateSelectLink: true, linkName: escalation.linkName, buttonAriaLabel: getLocalSpecificString("escalationSelectLinkAriaLabel")
                                .replace("{title}", () => escalation.title), disabled: !escalation.link, isRecommended: isAIRecommendationEnabled && recommendedCardId === escalation.cardId, recommendedLabel: getLocalSpecificString("cardRecommendationAIRecommended"), onNavigateStart: escalation.cardId === "consumer"
                                ? () => announce(getLocalSpecificString("consumerMetadataLoadingAnnouncement"))
                                : undefined }) }), escalation.name));
                })] })) })));
};
export default Escalations;
